import { CHAPTERS, type Question, type Section } from "./data"
import { QUESTIONS } from "./questions"
import { getHistory } from "./storage"

// ============================================================
// STUDY SIGNALS
// Shared helpers that compute per-section coverage and strength
// signals from the answer history. The thresholds defer all
// strength claims until the user has seen at least half of the
// bank, and per-section claims wait until that section has been
// studied with both breadth (60% questions seen) and depth (2x
// the section's question count in attempts).
// ============================================================

export const STRONG_ACCURACY_GATE = 0.9
export const SECTION_SEEN_GATE = 0.6
export const SECTION_ATTEMPTS_MULTIPLIER = 2
export const BANK_SEEN_GATE = 0.5

export interface SectionCoverage {
  section: Section
  title: string
  total: number
  seen: number
  attempts: number
  correct: number
}

export type SectionStatus = "not-started" | "building" | "practicing" | "strong"

export function activeQuestions(): Question[] {
  return QUESTIONS.filter((q) => q.status === "verified")
}

export function sectionCoverage(): SectionCoverage[] {
  const map = new Map<Section, SectionCoverage>()
  for (const q of activeQuestions()) {
    const sec = q.answer.section
    let cov = map.get(sec)
    if (!cov) {
      cov = { section: sec, title: CHAPTERS[sec].title, total: 0, seen: 0, attempts: 0, correct: 0 }
      map.set(sec, cov)
    }
    cov.total++
    const events = getHistory(q)
    if (events.length > 0) cov.seen++
    cov.attempts += events.length
    cov.correct += events.filter((e) => e.correct).length
  }
  return [...map.values()].sort((a, b) => a.section - b.section)
}

export function bankCoverage(): { total: number; seen: number } {
  let total = 0
  let seen = 0
  for (const q of activeQuestions()) {
    total++
    if (getHistory(q).length > 0) seen++
  }
  return { total, seen }
}

export function isBankGateMet(): boolean {
  const cov = bankCoverage()
  return cov.total > 0 && cov.seen / cov.total >= BANK_SEEN_GATE
}

export function isSectionGateMet(cov: SectionCoverage): boolean {
  if (cov.total === 0) return false
  return cov.seen / cov.total >= SECTION_SEEN_GATE && cov.attempts >= SECTION_ATTEMPTS_MULTIPLIER * cov.total
}

export function sectionStatus(cov: SectionCoverage): SectionStatus {
  if (cov.attempts === 0) return "not-started"
  if (!isBankGateMet() || !isSectionGateMet(cov)) return "building"
  return cov.correct / cov.attempts >= STRONG_ACCURACY_GATE ? "strong" : "practicing"
}
